import type { Diagnostic, Fix, Severity } from '../engine/types';

export const SEVERITY_RANK: Record<Severity, number> = { error: 0, warning: 1, info: 2 };
export const SEVERITIES: readonly Severity[] = ['error', 'warning', 'info'];

/** Size indexes, or 'all' when the diagnostic concerns every size. */
export type SizeSet = number[] | 'all';

export interface GroupMessage {
  text: string;
  sizes: SizeSet;
}

/** Diagnostics sharing (line_no, code): the unit the UI shows. */
export interface DiagnosticGroup {
  key: string;
  line_no: number;
  code: string;
  label: string;
  /** Most severe severity in the group. */
  severity: Severity;
  sizes: SizeSet;
  /** Deduplicated messages, each with the sizes it was emitted for. */
  messages: GroupMessage[];
  /** Union of fix ids, in first-seen order. */
  fixIds: string[];
  /**
   * 1.6.0 (ADR-013): the lines read by island the group's computation went through (union of `depends_on`,
   * ascending); empty when it rests on no island reading.
   */
  dependsOn: number[];
  /**
   * 1.6.0 (ADR-013): a warning lowered from an error because it rests on an island reading not yet confirmed
   * (`depends_on` not empty and `rule_version` « îlot/… »): it goes back to an error once a stated count confirms
   * the reading.
   */
  lowered: boolean;
  diagnostics: Diagnostic[];
}

/** A diagnostic resting on an island reading not yet confirmed, lowered to a warning (ADR-013, decision 6). */
export function isLowered(d: Diagnostic): boolean {
  return d.severity === 'warning' && (d.depends_on?.length ?? 0) > 0 && (d.rule_version ?? '').startsWith('îlot/');
}

function mergeSizes(sizes: Array<number | null>, sizeCount: number | undefined): SizeSet {
  if (sizes.some((s) => s === null)) return 'all';
  const uniq = [...new Set(sizes as number[])].sort((a, b) => a - b);
  if (sizeCount !== undefined && sizeCount > 1 && uniq.length >= sizeCount) return 'all';
  return uniq;
}

/**
 * Groups diagnostics by (line_no, code) and sorts groups by severity, then line, then code.
 * `sizeCount` lets a group emitted once per size collapse to 'all'.
 */
export function groupDiagnostics(diagnostics: readonly Diagnostic[], sizeCount?: number): DiagnosticGroup[] {
  const byKey = new Map<string, Diagnostic[]>();
  for (const d of diagnostics) {
    const key = `${d.line_no}\u0000${d.code}`;
    const list = byKey.get(key);
    if (list) list.push(d);
    else byKey.set(key, [d]);
  }
  const groups: DiagnosticGroup[] = [];
  for (const [key, list] of byKey) {
    const first = list[0];
    const severity = list.reduce<Severity>(
      (acc, d) => (SEVERITY_RANK[d.severity] < SEVERITY_RANK[acc] ? d.severity : acc),
      first.severity,
    );
    const fixIds: string[] = [];
    for (const d of list) for (const id of d.fix_ids) if (!fixIds.includes(id)) fixIds.push(id);
    const dependsOn = [...new Set(list.flatMap((d) => d.depends_on ?? []))].sort((a, b) => a - b);
    groups.push({
      key,
      line_no: first.line_no,
      code: first.code,
      label: first.label,
      severity,
      sizes: mergeSizes(
        list.map((d) => d.size),
        sizeCount,
      ),
      messages: dedupMessages(list, sizeCount),
      fixIds,
      dependsOn,
      lowered: severity === 'warning' && list.some(isLowered),
      diagnostics: list,
    });
  }
  return groups.sort(compareGroups);
}

/** Distinct messages of `diagnostics` (first-seen order), each with the sizes it was emitted for. */
export function dedupMessages(diagnostics: readonly Diagnostic[], sizeCount?: number): GroupMessage[] {
  const bySize = new Map<string, Array<number | null>>();
  for (const d of diagnostics) {
    const arr = bySize.get(d.message);
    if (arr) arr.push(d.size);
    else bySize.set(d.message, [d.size]);
  }
  return [...bySize].map(([text, sizes]) => ({ text, sizes: mergeSizes(sizes, sizeCount) }));
}

/** Diagnostics carrying structured values (expected and/or actual). */
export function hasValues(d: Diagnostic): boolean {
  return (d.expected !== null && d.expected !== undefined) || (d.actual !== null && d.actual !== undefined);
}

export function compareGroups(a: DiagnosticGroup, b: DiagnosticGroup): number {
  return (
    SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
    a.line_no - b.line_no ||
    (a.code < b.code ? -1 : a.code > b.code ? 1 : 0)
  );
}

export function countBySeverity(groups: readonly DiagnosticGroup[]): Record<Severity, number> {
  const counts: Record<Severity, number> = { error: 0, warning: 0, info: 0 };
  for (const g of groups) counts[g.severity] += 1;
  return counts;
}

export function groupConcernsSize(group: DiagnosticGroup, size: number): boolean {
  return group.sizes === 'all' || group.sizes.includes(size);
}

export interface GroupFilter {
  severities: ReadonlySet<Severity>;
  /** When set, keep only groups that concern this size. */
  size: number | null;
}

export function filterGroups(groups: readonly DiagnosticGroup[], filter: GroupFilter): DiagnosticGroup[] {
  return groups.filter(
    (g) => filter.severities.has(g.severity) && (filter.size === null || groupConcernsSize(g, filter.size)),
  );
}

/** Per source line: the most severe severity and the groups on that line (line 0 excluded). */
export function groupsByLine(groups: readonly DiagnosticGroup[]): Map<number, { severity: Severity; groups: DiagnosticGroup[] }> {
  const map = new Map<number, { severity: Severity; groups: DiagnosticGroup[] }>();
  for (const g of groups) {
    if (g.line_no < 1) continue;
    const entry = map.get(g.line_no);
    if (!entry) map.set(g.line_no, { severity: g.severity, groups: [g] });
    else {
      entry.groups.push(g);
      if (SEVERITY_RANK[g.severity] < SEVERITY_RANK[entry.severity]) entry.severity = g.severity;
    }
  }
  return map;
}

export function fixesForGroup(group: DiagnosticGroup, fixesById: ReadonlyMap<string, Fix>): Fix[] {
  return group.fixIds.map((id) => fixesById.get(id)).filter((f): f is Fix => f !== undefined);
}
